import {ChangeDetectionStrategy, Component, computed, EventEmitter, inject, Input, OnDestroy, OnInit, signal} from '@angular/core';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  Router,
  RouterLink,
  UrlTree
} from '@angular/router';
import {BlurWhenDirective} from '@directives/blur-when.directive';
import {Subscription} from 'rxjs';
import {TabViewModel} from '@components/ui/tabs/tab-view.model';
import {TabListEntry} from '@components/ui/tabs/tab-list-entry';
import {TabList} from '@components/ui/tabs/tab-list';

@Component({
    selector: 'app-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, BlurWhenDirective]
})
export class TabsComponent implements OnInit, OnDestroy {

  private readonly DEFAULT_DROPDOWN_TITLE = 'Navigation menu';

  private readonly subscriptions: Subscription = new Subscription();

  @Input() style: string;
  tabList: TabList;
  readonly tabs = signal<TabViewModel[]>([]);
  readonly subtabs = signal<TabViewModel[]>([]);
  readonly dropdownOpen = signal(false);
  readonly dropdownTitle = signal(this.DEFAULT_DROPDOWN_TITLE);
  navigated: EventEmitter<void> = new EventEmitter();

  readonly hasTabs = computed(() => this.tabs().length > 0);
  readonly hasSubtabs = computed(() => this.subtabs().length > 0);
  readonly dropdownAriaLabel = computed(() => {
    const openClosed = this.dropdownOpen() ? 'Open' : 'Closed';
    return `${this.dropdownTitle()} clickable dropdown (${openClosed})`;
  });

  private readonly router = inject(Router);

  ngOnInit(): void {
    const subscription = this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
        this.navigated.emit();
      }

      if (event instanceof NavigationEnd) {
        this.updateHighlightingForUrl();
      }
    });
    this.subscriptions.add(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  toggleDropdown(): void {
    this.dropdownOpen.update(v => !v);
  }

  setTabList(tabList: TabList) {
    this.tabList = tabList;
    this.rebuildViewModels();
    this.updateHighlightingForUrl();
  }

  rebuildViewModels() {
    this.tabs.set(this.convertTabListEntriesToViewModels(this.tabList.entries));
  }

  private convertTabListEntriesToViewModels(tabListEntries: TabListEntry[]): TabViewModel[] {
    return tabListEntries.map(entry => this.convertTabListEntryToViewModel(entry));
  }

  private convertTabListEntryToViewModel(tab: TabListEntry): TabViewModel {
    const children = this.convertTabListEntriesToViewModels(tab.children);
    const urlTree = this.router.createUrlTree(tab.routerLinkCommands);

    return {
      id: tab.id,
      title: tab.title,
      ariaLabel: 'Dropdown item ' + tab.title,
      routerLinkCommands: tab.routerLinkCommands,
      children: children,
      urlTree: urlTree,
      active: false,
      highlighted: false
    };
  }

  private updateHighlightingForUrl() {
    if (!this.hasTabs()) {
      return;
    }

    const path = this.findActiveTabPath(this.tabs());

    if (path) {
      const activeTab = path.at(-1);
      this.subtabs.set(path[0].children);
      this.dropdownTitle.set(activeTab.title);
      this.updateTabHighlighting(this.tabs(), activeTab);
    } else {
      this.subtabs.set([]);
      this.dropdownTitle.set(this.DEFAULT_DROPDOWN_TITLE);
      this.updateTabHighlighting(this.tabs(), null);
      this.router.navigate(this.tabList.entries[0].routerLinkCommands);
    }
  }

  /**
   * Finds the path to the active tab in a hierarchy of tabs, where active means that the tab's URL is an exact match for the Angular router's current URL
   *
   * @remarks
   * Performs a depth-first search and immediately returns on the first match found, ignoring the rest of tab hierarchy
   *
   * @param tabs - The hierarchy of tabs to recursively search
   * @returns The path to reach the active tab, or undefined if none of the tabs in the given hierarchy are active
   */
  private findActiveTabPath(tabs: TabViewModel[]): TabViewModel[] | undefined {
    for (const tab of tabs) {
      const tabIsActive = this.isUrlTreeActive(tab.urlTree);

      if (tabIsActive) {
        return [tab];
      }

      const possibleActiveChildPath = this.findActiveTabPath(tab.children);

      if (possibleActiveChildPath) {
        return [tab, ...possibleActiveChildPath];
      }
    }

    return undefined;
  }

  private isUrlTreeActive(urlTree: UrlTree): boolean {
    const targetPath: string = this.router.serializeUrl(urlTree).split(/[?#]/)[0];
    const currentPath: string = this.router.url.split(/[?#]/)[0];
    return targetPath === currentPath;
  }

  /**
   * Updates the active highlight state in a hierarchy of tabs, where an active child tab will also highlight the parent tab
   *
   * @param tabs - The hierarchy of tabs to recursively update
   * @param activeTab - The tab that is currently active, if available
   * @returns true if activeTab is present in the hierarchy of tabs; otherwise false
   */
  private updateTabHighlighting(tabs: TabViewModel[], activeTab?: TabViewModel): boolean {
    return tabs.reduce((found, tab) => {
      const isTabActive = tab === activeTab;
      const childIsActive = this.updateTabHighlighting(tab.children, activeTab);

      tab.active = isTabActive;
      tab.highlighted = isTabActive || childIsActive;

      return found || tab.highlighted;
    }, false);
  }
}
